import type { PLAAttributeDefinition, PLACubeDimensionDefinition, PLAElementRecord, PLAElementTypeDefinition, PLAProject } from "./PLAState";
import { comparePLAElementsByDefaultOrder } from "./PLAElementSorting";

export type PLACubeViewSettings = {
   dimensionId?: string;
   memberId?: string;
   latest?: boolean;
   resetVersion?: number;
};

export const PLA_CUBE_ANY_FILTER = "__PLA_CUBE_ANY__";

export function isPLACubeDimensionTotalEnabled(dimension: PLACubeDimensionDefinition, elementTypes: Record<string, PLAElementTypeDefinition>): boolean {
   const configuredValue = elementTypes[dimension.elementTypeId]?.total;
   if (configuredValue !== undefined) return configuredValue;
   return (dimension as PLACubeDimensionDefinition & { total?: boolean }).total !== false;
}

export function migratePLACubeTotalsToElementTypes(project: PLAProject): PLAProject {
   const legacyDisabledTypes = new Set<string>();
   Object.values(project.elementTypes || {}).forEach((elementType) => {
      Object.values(elementType.attributes || {}).forEach((attribute) => {
         if (attribute.type !== "cube") return;
         attribute.cubeDimensions?.forEach((dimension) => {
            if ((dimension as PLACubeDimensionDefinition & { total?: boolean }).total === false) {
               legacyDisabledTypes.add(dimension.elementTypeId);
            }
         });
      });
   });

   let changed = false;
   const elementTypes = Object.fromEntries(Object.entries(project.elementTypes || {}).map(([elementTypeId, elementType]) => {
      let attributesChanged = false;
      const attributes = Object.fromEntries(Object.entries(elementType.attributes || {}).map(([attributeId, attribute]) => {
         if (attribute.type !== "cube" || !attribute.cubeDimensions?.some((dimension) => "total" in dimension)) return [attributeId, attribute];
         attributesChanged = true;
         const cubeDimensions = attribute.cubeDimensions.map((dimension) => dimension.expanded === undefined
            ? { elementTypeId: dimension.elementTypeId }
            : { elementTypeId: dimension.elementTypeId, expanded: dimension.expanded });
         return [attributeId, { ...attribute, cubeDimensions }];
      }));
      const total = elementType.total ?? (legacyDisabledTypes.has(elementTypeId) ? false : undefined);
      const nextElementType = attributesChanged || total !== elementType.total
         ? { ...elementType, total, ...(attributesChanged ? { attributes } : {}) }
         : elementType;
      if (nextElementType !== elementType) changed = true;
      return [elementTypeId, nextElementType];
   }));
   return changed ? { ...project, elementTypes } : project;
}

export function normalizePLACubeViewSettings(settings: PLACubeViewSettings, dimensionId: string | undefined, elements: PLAElementRecord[]): PLACubeViewSettings {
   return {
      ...settings,
      dimensionId: dimensionId || undefined,
      memberId: dimensionId && elements.some((element) => element.elementType === dimensionId && element.id === settings.memberId) ? settings.memberId : undefined,
      latest: Boolean(dimensionId && settings.latest)
   };
}

export function changePLACubeTimeDimension(settings: PLACubeViewSettings, dimensionId: string): PLACubeViewSettings {
   return { dimensionId, memberId: undefined, latest: false, resetVersion: (settings.resetVersion || 0) + 1 };
}

export function getPLACubePhaseElements(elements: PLAElementRecord[], dimensionId: string): PLAElementRecord[] {
   return elements.filter((element) => element.elementType === dimensionId).sort(comparePLAElementsByDefaultOrder);
}

export function getPLACubePhaseColor(index: number): string {
   return `hsl(${(index * 137.508) % 360}, 65%, 85%)`;
}

export function getPLACubePhaseTextColor(index: number): string {
   return `hsl(${(index * 137.508) % 360}, 65%, 38%)`;
}

export function applyPLACubeGlobalFilter(definition: PLAAttributeDefinition, filter: Record<number, string> | undefined, settings: PLACubeViewSettings): Record<number, string> | undefined {
   const index = definition.cubeDimensions?.findIndex((dimension) => dimension.elementTypeId === settings.dimensionId) ?? -1;
   return index < 0 || !settings.memberId ? filter : { ...filter, [index]: settings.memberId };
}

function hasPLACubeMemberFilter(filter: Record<number, string> | undefined, index: number): boolean {
   const memberId = filter?.[index];
   return Boolean(memberId && memberId !== PLA_CUBE_ANY_FILTER);
}

export function canShowPLACubeTotals(definition: PLAAttributeDefinition, filter: Record<number, string> | undefined, settings: PLACubeViewSettings = {}, elementTypes: Record<string, PLAElementTypeDefinition> = {}): boolean {
   return (definition.cubeDimensions || []).every((dimension, index) =>
      isPLACubeDimensionTotalEnabled(dimension, elementTypes) ||
      hasPLACubeMemberFilter(filter, index) ||
      Boolean(settings.latest && dimension.elementTypeId === settings.dimensionId)
   );
}

export function findPLACubeLatestCell<T>(keys: string[], dimensionIndex: number, phases: PLAElementRecord[], memberId: string | undefined, getValue: (keys: string[]) => T): { value: T; phase: PLAElementRecord; color: string } | undefined {
   const cutoff = memberId ? phases.findIndex((phase) => phase.id === memberId) : phases.length - 1;
   for (let index = cutoff; index >= 0; index--) {
      const coordinates = keys.map((key, position) => position === dimensionIndex ? phases[index].id : key);
      const value = getValue(coordinates);
      if (value !== undefined && value !== null && value !== "" && value !== 0) return { value, phase: phases[index], color: getPLACubePhaseColor(index) };
   }
   return undefined;
}
